import { UserRef } from './bewerber';

// Email Status enum matching backend
export enum EmailStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  DELIVERED = 'DELIVERED',
  BOUNCED = 'BOUNCED',
}

// Incoming Email Status enum matching backend
export enum IncomingEmailStatus {
  UNPROCESSED = 'UNPROCESSED',
  PROCESSED = 'PROCESSED',
  LINKED = 'LINKED',
  IGNORED = 'IGNORED',
  ERROR = 'ERROR',
}

// Bewerber reference for emails
export interface BewerberRef {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

// Bedarf reference for emails
export interface BedarfRef {
  id: string;
  childFirstName: string;
  childLastName: string;
  parentEmail?: string;
}

// Email Template interface
export interface EmailTemplate {
  id: string;
  name: string;
  description: string | null;
  subject: string;
  body: string;
  bodyText: string | null;
  variables: Record<string, unknown> | null;
  isActive: boolean;
  isSystem: boolean;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: UserRef;
}

// Email Log interface
export interface EmailLog {
  id: string;
  templateId: string | null;
  recipients: string[];
  ccRecipients: string[];
  bccRecipients: string[];
  subject: string;
  bodyHtml: string;
  bodyText: string | null;
  status: EmailStatus;
  sentAt: string | null;
  error: string | null;
  messageId: string | null;
  imapUid: string | null;
  metadata: Record<string, unknown> | null;
  bewerberId: string | null;
  bedarfId: string | null;
  createdById: string;
  createdAt: string;
  template?: { id: string; name: string } | null;
  bewerber?: BewerberRef | null;
  bedarf?: BedarfRef | null;
  createdBy?: UserRef;
  incomingReplies?: IncomingEmail[];
}

// Incoming Email interface
export interface IncomingEmail {
  id: string;
  messageId: string;
  imapUid: string;
  fromAddress: string;
  fromName: string | null;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string;
  bodyHtml: string | null;
  bodyText: string | null;
  receivedAt: string;
  processedAt: string | null;
  status: IncomingEmailStatus;
  bewerberId: string | null;
  bewerber?: BewerberRef | null;
  inReplyToId: string | null;
  inReplyTo?: EmailLog | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

// Rendered Email (preview result)
export interface RenderedEmail {
  subject: string;
  html: string;
  text: string | null;
}

// Create Template DTO
export interface CreateTemplateDto {
  name: string;
  description?: string;
  subject: string;
  body: string;
  bodyText?: string;
  variables?: Record<string, unknown>;
  isActive?: boolean;
}

// Update Template DTO
export interface UpdateTemplateDto {
  name?: string;
  description?: string;
  subject?: string;
  body?: string;
  bodyText?: string;
  variables?: Record<string, unknown>;
  isActive?: boolean;
}

// Send Email DTO (direct)
export interface SendEmailDto {
  recipients: string[];
  ccRecipients?: string[];
  bccRecipients?: string[];
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  bewerberId?: string;
  bedarfId?: string;
}

// Send Template Email DTO
export interface SendTemplateEmailDto {
  templateName: string;
  recipients: string[];
  ccRecipients?: string[];
  bccRecipients?: string[];
  data: Record<string, unknown>;
  bewerberId?: string;
  bedarfId?: string;
}

// Send Bewerber Email DTO
export interface SendBewerberEmailDto {
  templateName: string;
  data?: Record<string, unknown>;
  bodyHtml?: string;
  subject?: string;
}

// Query Email Logs DTO
export interface QueryEmailLogDto {
  status?: EmailStatus;
  bewerberId?: string;
  bedarfId?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'createdAt' | 'sentAt' | 'subject';
  sortOrder?: 'asc' | 'desc';
}

// Query Incoming Emails DTO
export interface QueryIncomingEmailDto {
  status?: IncomingEmailStatus;
  bewerberId?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: 'receivedAt' | 'fromAddress' | 'subject';
  sortOrder?: 'asc' | 'desc';
}

// Link Incoming Email DTO
export interface LinkEmailDto {
  bewerberId: string;
}

// API response wrapper (reusing from bewerber)
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Email Status display configuration
export const EmailStatusConfig: Record<EmailStatus, {
  label: string;
  color: string;
  bgColor: string;
}> = {
  [EmailStatus.PENDING]: {
    label: 'Ausstehend',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
  },
  [EmailStatus.SENT]: {
    label: 'Gesendet',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  [EmailStatus.FAILED]: {
    label: 'Fehlgeschlagen',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  [EmailStatus.DELIVERED]: {
    label: 'Zugestellt',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  [EmailStatus.BOUNCED]: {
    label: 'Unzustellbar',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
};

// Incoming Email Status display configuration
export const IncomingEmailStatusConfig: Record<IncomingEmailStatus, {
  label: string;
  color: string;
  bgColor: string;
}> = {
  [IncomingEmailStatus.UNPROCESSED]: {
    label: 'Unverarbeitet',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  },
  [IncomingEmailStatus.PROCESSED]: {
    label: 'Verarbeitet',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  [IncomingEmailStatus.LINKED]: {
    label: 'Verknüpft',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  [IncomingEmailStatus.IGNORED]: {
    label: 'Ignoriert',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
  },
  [IncomingEmailStatus.ERROR]: {
    label: 'Fehler',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
};
